import { useEffect, useState } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { IPublicClientApplication } from '@azure/msal-browser';
import SessionList from './components/SessionList';
import AdminSettingsPage from './components/AdminSettingsPage';
import { buildLoginRequest } from './services/authConfig';
import { PublicAppConfig } from './types/appConfig';
import joskoLogo from './assets/josko-logo.svg';

type AppView = 'sessions' | 'admin';

function SignInButton({ config }: { config: PublicAppConfig }) {
  const { instance } = useMsal();

  const handleLogin = () => {
    instance.loginPopup(buildLoginRequest(config.frontend.azureTenantId)).catch((error) => {
      console.error('Login failed:', error);
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4 py-8">
      <section className="panel w-full max-w-md p-8">
        <img src={joskoLogo} alt="Josko" className="h-14 w-auto" />
        <div className="mt-8">
          <p className="eyebrow">AVD Session Manager</p>
          <h1 className="mt-3 text-3xl font-semibold text-ink">Sitzungen verwalten</h1>
        </div>
        <button className="btn-primary mt-8 w-full" type="button" onClick={handleLogin}>
          Mit Microsoft anmelden
        </button>
        <p className="mt-5 text-sm text-muted">Version {config.version}</p>
      </section>
    </div>
  );
}

function SignOutButton() {
  const { instance } = useMsal();

  const handleLogout = () => {
    instance.logoutPopup().catch((error) => {
      console.error('Logout failed:', error);
    });
  };

  return (
    <button onClick={handleLogout} className="btn-secondary" type="button">
      Abmelden
    </button>
  );
}

function MainContent({ version }: { version: string }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState<AppView>(() =>
    window.location.hash === '#admin' ? 'admin' : 'sessions'
  );

  useEffect(() => {
    const handleHashChange = () => {
      setView(window.location.hash === '#admin' ? 'admin' : 'sessions');
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (!isAdmin && view === 'admin') {
      window.location.hash = '';
      setView('sessions');
    }
  }, [isAdmin, view]);

  const navigateTo = (nextView: AppView) => {
    setView(nextView);
    window.location.hash = nextView === 'admin' ? 'admin' : '';
  };

  return (
    <div className="min-h-screen bg-page">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-5">
            <img src={joskoLogo} alt="Josko" className="h-12 w-auto" />
            <div className="border-l border-line pl-5">
              <p className="eyebrow">AVD Session Manager</p>
              <h1 className="mt-1 text-xl font-semibold text-ink">Sitzungsverwaltung</h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              className={view === 'sessions' ? 'tab-active' : 'tab'}
              type="button"
              onClick={() => navigateTo('sessions')}
            >
              Sitzungen
            </button>
            {isAdmin && (
              <button
                className={view === 'admin' ? 'tab-active' : 'tab'}
                type="button"
                onClick={() => navigateTo('admin')}
              >
                Administration
              </button>
            )}
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {view === 'admin' && isAdmin ? (
          <AdminSettingsPage />
        ) : (
          <SessionList onAdminChange={setIsAdmin} />
        )}
      </main>

      <footer className="mx-auto max-w-7xl px-4 pb-8 text-sm text-muted sm:px-6 lg:px-8">
        Version {version}
      </footer>
    </div>
  );
}

interface AppProps {
  pca: IPublicClientApplication;
  config: PublicAppConfig;
}

export default function App({ pca, config }: AppProps) {
  return (
    <MsalProvider instance={pca}>
      <AuthenticatedTemplate>
        <MainContent version={config.version} />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInButton config={config} />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}
